<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Stock Levels</h1>
          <p class="lede">{{ rows.length }} variant{{ rows.length === 1 ? '' : 's' }} tracked{{ lowOnly ? ' · low-stock only' : '' }}.</p>
        </div>
        <div class="head-actions">
          <label class="inline">
            <input type="checkbox" v-model="lowOnly" @change="load" style="width:auto; margin-right:6px" />
            Low stock only
          </label>
          <button class="secondary" @click="load">Refresh</button>
        </div>
      </div>
      <div class="card">
        <table>
          <thead>
            <tr>
              <th>Product</th><th>Variant</th><th>SKU</th><th>Shop</th>
              <th style="text-align:right">On hand</th><th style="text-align:right">Reorder pt</th>
              <th style="text-align:right">Value at cost</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id" :class="{low: r.on_hand <= r.reorder_point}">
              <td>{{ r.product_name }}</td>
              <td>{{ r.variant_name }}</td>
              <td class="muted small">{{ r.sku }}</td>
              <td>{{ r.shop_name }}</td>
              <td style="text-align:right"><strong>{{ r.on_hand }}</strong></td>
              <td style="text-align:right">{{ r.reorder_point }}</td>
              <td style="text-align:right">KES {{ format(r.on_hand * r.cost_price) }}</td>
              <td>
                <button v-if="can('stock.adjust')" class="secondary small" @click="openAdjust(r)">Adjust</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!rows.length" class="muted center">No stock records. Add products and their initial stock in the <strong>Products</strong> page.</p>
      </div>
    </div>

    <div v-if="adjust" class="modal-back" @click.self="adjust=null">
      <div class="modal card">
        <h3>Adjust Stock &mdash; {{ adjust.product_name }} / {{ adjust.variant_name }}</h3>
        <p class="muted">Current on-hand: <strong>{{ adjust.on_hand }}</strong></p>
        <label>Quantity change (+add or −remove)</label>
        <input type="number" v-model.number="adjust.qty" />
        <label>Reason (required)</label>
        <select v-model="adjust.reason">
          <option value="damaged">Damaged</option>
          <option value="stock_take_correction">Stock take correction</option>
          <option value="sample_gift">Sample / gift</option>
          <option value="return_to_supplier">Return to supplier</option>
          <option value="opening_balance">Opening balance</option>
          <option value="other">Other (specify in notes)</option>
        </select>
        <label>Notes (optional)</label>
        <input v-model="adjust.notes" />
        <div class="modal-actions">
          <button class="secondary" @click="adjust=null">Cancel</button>
          <button class="primary" @click="submitAdjust" :disabled="!adjust.qty || !adjust.reason || submitting">
            {{ submitting ? 'Saving…' : 'Save adjustment' }}
          </button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can } from '@/api';

export default {
  name: 'StockManagement',
  components: { Navigation },
  data() { return { rows: [], lowOnly: false, adjust: null, submitting: false, err: '' }; },
  async mounted() { await this.load(); },
  methods: {
    can(k) { return can(k); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const { data } = await execute({ function: 'getStockLevels', lowOnly: this.lowOnly ? 1 : 0 }, paysol);
      if (data.success) this.rows = data.data;
    },
    openAdjust(r) { this.adjust = { ...r, qty: 0, reason: '', notes: '' }; this.err = ''; },
    async submitAdjust() {
      this.submitting = true; this.err = '';
      try {
        const reason = (this.adjust.reason === 'other' && this.adjust.notes) ? this.adjust.notes : this.adjust.reason;
        const { data } = await execute({
          function: 'adjustStock',
          shopId: this.adjust.shop_id, variantId: this.adjust.variant_id,
          quantity: this.adjust.qty, reason,
        }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.adjust = null;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Adjustment failed.'; }
      finally { this.submitting = false; }
    }
  }
};
</script>

<style scoped>
.wrap { max-width: 1200px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; }
.lede { color: var(--paysol-muted); margin: 0; }
.head-actions { display: flex; gap: 12px; align-items: center; }
.inline { display: inline-flex; align-items: center; margin: 0; font-size: 12px; color: var(--paysol-muted); text-transform: none; letter-spacing: 0; font-weight: 500; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
tr.low td { background: rgba(245, 158, 11, 0.06); }
tr.low td:first-child { border-left: 2px solid var(--paysol-warning); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
button.small { padding: 4px 10px; font-size: 11px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
</style>
